import { mergeClasses } from "@fluentui/react-components";
import { useListenStyles } from "./listenStil";
import { SCHADENSARTEN } from "./enums";
import { asArray } from "./structuredAnswers";
import { ziffernFilter } from "./GrossZahlEingabe";

/**
 * Schadenhistorie als Tabelle (xlsx Z. 7, Punkt 4b): Schadenart, Schadentag,
 * Schadenhöhe, Bemerkung — je Schaden eine Zeile.
 *
 * Bis hierher gab es dafür ein einziges Freitextfeld `schaedenDetails`. Das
 * bleibt als Alt-Key erhalten (Bestandsdaten auf PROD) und wird im Formular als
 * „ergänzende Bemerkungen" weitergeführt; die strukturierten Zeilen liegen neu
 * unter `schaeden`.
 *
 * Die gesamte Liste liegt unter **einem** Answer-Key — wie bei
 * `BeteiligtenListe`: `PUT /risk-objects/:id` ist ein Replace-Set und die
 * Detailansicht ordnet Merkmale über den Key-Präfix zu; ein Key je Zeile würde
 * beides brechen. Der Schadentag ist ein echtes Datumsfeld, damit die Angabe
 * später auswertbar bleibt.
 */

const useStyles = useListenStyles;

/** Für `includes` mit beliebigem String — der Katalog selbst ist ein Tupel. */
const SCHADENSART_LISTE: readonly string[] = SCHADENSARTEN;

export type SchadenEintrag = {
  id: string;
  /** Eine der `SCHADENSARTEN`; Alt-/Fremdwerte werden angezeigt, nicht verworfen. */
  art?: string;
  /** ISO-Datum (`type="date"`). */
  tag?: string;
  /** Schadenhöhe in Euro, brutto. */
  hoehe?: string;
  bemerkung?: string;
};

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const str = (v: unknown): string | undefined =>
  typeof v === "string" && v !== "" ? v : undefined;

/** Liste aus einem Answer-Wert lesen; alles Unbrauchbare fällt heraus. Idempotent. */
export const asSchadenListe = (value: unknown): SchadenEintrag[] =>
  asArray<unknown>(value)
    .filter(isObject)
    .map((e, idx) => {
      const eintrag: SchadenEintrag = {
        id: str(e.id) ?? `s-${idx}`,
      };
      const art = str(e.art);
      const tag = str(e.tag);
      const hoehe = str(e.hoehe);
      const bemerkung = str(e.bemerkung);
      if (art) eintrag.art = art;
      if (tag) eintrag.tag = tag;
      if (hoehe) eintrag.hoehe = hoehe;
      if (bemerkung) eintrag.bemerkung = bemerkung;
      return eintrag;
    });

export const newSchadenId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `s-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

type Props = {
  entries: SchadenEintrag[];
  onChange: (next: SchadenEintrag[]) => void;
  intro?: string;
};

const SchadenListe = ({ entries, onChange, intro }: Props) => {
  const styles = useStyles();

  const update = (id: string, patch: Partial<SchadenEintrag>) =>
    onChange(entries.map((e) => (e.id === id ? { ...e, ...patch } : e)));

  const remove = (id: string) => onChange(entries.filter((e) => e.id !== id));

  const add = () => onChange([...entries, { id: newSchadenId() }]);

  return (
    <div className={styles.root}>
      {intro && <span className={styles.intro}>{intro}</span>}
      {entries.length === 0 && (
        <span className={styles.empty}>Noch kein Schaden erfasst.</span>
      )}
      {entries.map((entry, idx) => (
        <div key={entry.id} className={styles.card}>
          <div className={styles.head}>
            <span className={styles.badge}>{idx + 1}</span>
            <span className={styles.title}>{entry.art || `Schaden ${idx + 1}`}</span>
            <button
              type="button"
              className={styles.removeBtn}
              onClick={() => remove(entry.id)}
              aria-label="Schaden entfernen"
            >
              ×
            </button>
          </div>
          <div className={styles.grid}>
            <div className={styles.field}>
              <span className={styles.label}>Schadenart</span>
              <select
                className={styles.input}
                name={`schaeden-${idx}-art`}
                aria-label={`Schaden ${idx + 1} — Schadenart`}
                value={entry.art ?? ""}
                onChange={(e) => update(entry.id, { art: e.target.value })}
              >
                <option value="">Bitte wählen</option>
                {SCHADENSARTEN.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
                {/* Ein gespeicherter Wert außerhalb des Katalogs bliebe sonst
                    beim Öffnen still verworfen. */}
                {entry.art && !SCHADENSART_LISTE.includes(entry.art) && (
                  <option value={entry.art}>{entry.art}</option>
                )}
              </select>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Schadentag</span>
              <input
                type="date"
                className={styles.input}
                name={`schaeden-${idx}-tag`}
                aria-label={`Schaden ${idx + 1} — Schadentag`}
                value={entry.tag ?? ""}
                onChange={(e) => update(entry.id, { tag: e.target.value })}
              />
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Schadenhöhe (€, brutto)</span>
              <input
                className={styles.input}
                name={`schaeden-${idx}-hoehe`}
                aria-label={`Schaden ${idx + 1} — Schadenhöhe`}
                inputMode="decimal"
                placeholder="z. B. 4500"
                value={entry.hoehe ?? ""}
                onChange={(e) => update(entry.id, { hoehe: ziffernFilter(e.target.value, { dezimal: true }) })}
              />
            </div>
            <div className={mergeClasses(styles.field, styles.fullSpan)}>
              <span className={styles.label}>Bemerkung</span>
              <input
                className={styles.input}
                name={`schaeden-${idx}-bemerkung`}
                aria-label={`Schaden ${idx + 1} — Bemerkung`}
                value={entry.bemerkung ?? ""}
                onChange={(e) => update(entry.id, { bemerkung: e.target.value })}
              />
            </div>
          </div>
        </div>
      ))}
      <button type="button" className={styles.addBtn} onClick={add}>
        + Schaden hinzufügen
      </button>
    </div>
  );
};

export default SchadenListe;
